import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useRef, useState } from "react";
import type { Item } from "@instead/shared";
import { api } from "../lib/api.ts";
import { useT } from "../lib/i18n.ts";
import { DealCard, useReorder } from "../components/DealCard.tsx";
import { Ic } from "../components/Icons.tsx";
import { Dialog, Toast, useToast } from "../components/Ui.tsx";

const NO_FILTERS = { timeMin: null, moods: [], orientations: [], where: { kind: "anywhere" as const } };

/** A saved selection at /set/<id> (owner-only). Copy link lives here; the
 *  edit view has the name (edit icon focuses it), drag to reorder, redraw /
 *  replace / remove per card, Add a card (deals one in), Delete selection. */
export function SetView() {
  const t = useT();
  const qc = useQueryClient();
  const nav = useNavigate();
  const { id } = useParams({ from: "/set/$id" });
  const { data: sel } = useQuery({ queryKey: ["selections", id], queryFn: () => api.selections.get(id) });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: api.settings.get });
  const { data: catData } = useQuery({ queryKey: ["categories"], queryFn: api.categories.list });
  const [editing, setEditing] = useState(false);
  const [ids, setIds] = useState<string[] | null>(null);
  const [title, setTitle] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [toast, say] = useToast();
  const nameRef = useRef<HTMLInputElement>(null);
  const current = ids ?? sel?.itemIds ?? [];
  const { data: items } = useQuery({ queryKey: ["items", "byIds", current.join(",")], queryFn: () => api.items.byIds(current), enabled: current.length > 0, placeholderData: keepPreviousData });
  const { move, handleProps, cardProps } = useReorder(current, setIds);
  const cats = catData?.categories ?? [];
  const max = Math.min(5, settings?.rest.maxCards ?? 5);

  if (!sel) return null;
  const shownTitle = title ?? (sel.origin === "ai" && sel.title === "Picked with your AI" ? t("set.aiTitle") : sel.title);

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/set/${id}`);
      say(t("copied"));
    } catch {
      // clipboard unavailable — the address bar has it
    }
  }

  async function persist() {
    const updated = await api.selections.update(id, { itemIds: current, ...(title !== null ? { title: title.trim() || sel!.title } : {}) });
    qc.setQueryData(["selections", id], updated); // in place: no flip back to the old order while refetching
    await qc.invalidateQueries({ queryKey: ["selections"], exact: true });
  }

  async function saveChanges() {
    await persist();
    setEditing(false);
    setIds(null);
    setTitle(null);
    say(t("saved"));
  }

  async function addOne() {
    const r = await api.picks.shuffle({ count: 1, filters: NO_FILTERS, persist: false, excludeItemIds: current });
    if (r.itemIds[0]) setIds([...current, r.itemIds[0]]);
  }

  async function redraw(itemId: string) {
    const r = await api.picks.shuffle({ count: 1, filters: NO_FILTERS, persist: false, excludeItemIds: current });
    if (r.itemIds[0]) setIds(current.map((x) => (x === itemId ? r.itemIds[0]! : x)));
  }

  /** Replace one card by hand: keep what's on screen, then pick on the shelf. */
  async function replace(itemId: string) {
    await persist();
    setIds(null);
    setTitle(null);
    await nav({ to: "/pick", search: { into: "set", set: id, replace: itemId } as never });
  }

  async function remove() {
    await api.selections.remove(id);
    await qc.invalidateQueries({ queryKey: ["selections"] });
    await nav({ to: "/rest" });
  }

  const open = async (item: Item) => {
    await api.items.open(item.id);
    void qc.invalidateQueries({ queryKey: ["items"] });
  };

  return (
    <div className="rest-col">
      <div className="screen-head" style={{ marginBottom: "var(--sp-1)" }}>
        <button className="icon-btn" aria-label={t("back")} onClick={() => (editing ? (setEditing(false), setIds(null), setTitle(null)) : nav({ to: "/rest" }))}>{editing ? t("cancel") : Ic.back()}</button>
        <span style={{ flex: 1 }} />
        {editing ? (
          <button className="btn btn-primary" style={{ minHeight: 36 }} onClick={saveChanges}>{t("set.saveChanges")}</button>
        ) : (
          <>
            <button className="icon-btn" onClick={copyLink}>{Ic.link({ size: 14 })} {t("set.copyLink")}</button>
            <button className="icon-btn" onClick={() => setEditing(true)}>{Ic.edit({ size: 14 })} {t("edit")}</button>
          </>
        )}
      </div>

      {editing ? (
        <div className="title-line" style={{ justifyContent: "center" }}>
          <input ref={nameRef} aria-label={t("deal.name")} value={shownTitle} onChange={(e) => setTitle(e.target.value)} style={{ textAlign: "center", fontSize: "var(--fs-display)" }} maxLength={120} />
          <button type="button" className="icon-btn" style={{ minWidth: 32, minHeight: 32, padding: 0 }} aria-label={t("set.editName")} onClick={() => { nameRef.current?.focus(); nameRef.current?.select(); }}>{Ic.edit({ size: 14 })}</button>
        </div>
      ) : (
        <h1 className="display" style={{ textAlign: "center", marginBottom: "var(--sp-4)" }}>{shownTitle}</h1>
      )}

      <div className="deal">
        {(items ?? []).map((item) => (
          <DealCard key={item.id} item={item} cats={cats} mode={editing ? "edit" : "view"} onOpen={open}
            onRedraw={() => redraw(item.id)} onReplace={() => replace(item.id)} onRemove={() => setIds(current.filter((x) => x !== item.id))}
            onMove={(d) => move(item.id, d)} handleProps={handleProps(item.id, item.title)} cardProps={cardProps(item.id)} />
        ))}
      </div>

      {editing && (
        <>
          {current.length < max && (
            <button type="button" className="add-tile" style={{ marginTop: "var(--sp-3)" }} onClick={addOne}>{Ic.plus({ size: 16 })} {t("deal.addCard")}</button>
          )}
          <div style={{ display: "flex", justifyContent: "center", marginTop: "var(--sp-4)" }}>
            <button className="icon-btn danger" onClick={() => setConfirm(true)}>{Ic.trash({ size: 14 })} {t("set.delete")}</button>
          </div>
        </>
      )}

      {confirm && (
        <Dialog title={t("set.deleteQ", { name: shownTitle })} onClose={() => setConfirm(false)}>
          <div className="dialog-actions">
            <button className="btn" onClick={() => setConfirm(false)}>{t("keep")}</button>
            <button className="btn btn-danger" onClick={remove}>{t("delete")}</button>
          </div>
        </Dialog>
      )}
      <Toast msg={toast} />
    </div>
  );
}
